/**
 * InsurerAttention Page
 *
 * 県別注意事項 - マスタメンテナンス/県別注意事項
 * Search and display prefecture-specific insurer attention information.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@shared/providers/AuthContext';
import { useGlobalBehavior } from '@/shared/providers/GlobalBehaviorWrapper';
import { NotFound } from '@/pages/main/system/NotFound';
import {
  useMainInsurerAttentionInit,
  useMainInsurerAttentionSearch,
  useMainInsurerAttentionDetail,
  useMainInsurerAttentionDownload,
} from '@/hooks/main/useMainInsurerAttention';
import { AppAlert, AppSpin } from '@/components/atoms';
import {
  InsurerAttention,
  InsurerAttentionSearchFilters,
  InsurerAttentionSearchPaginatedData,
  PaginationInfo,
} from './types';
import { InsurerAttentionSearchForm } from './components/InsurerAttentionSearchForm';
import { InsurerAttentionResults } from './components/InsurerAttentionResults';
import { InsurerAttentionDetailModal } from './components/InsurerAttentionDetailModal';

const ERROR_MESSAGE = 'データの取得に失敗しました。';

type AttachmentKey = 'attachment1' | 'attachment2' | 'attachment3' | 'attachment4' | 'attachment5';

interface PageContent {
  attentions: InsurerAttention[];
  pagination: PaginationInfo | null;
}

const InsurerAttentionPage: React.FC = () => {
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();
  const { clinic } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [setEnterKeyNavigationEnabled]);

  const familySiteType = clinic?.parent_site_type || clinic?.site_type;
  const hasSearched = searchParams.has('searched');
  const currentPage = Number(searchParams.get('page')) || 1;

  const initialFilters = useMemo<InsurerAttentionSearchFilters>(
    () => ({
      insurer_number: searchParams.get('insurer_number') || '',
      insurer_name: searchParams.get('insurer_name') || '',
      insurer_name_kana: searchParams.get('insurer_name_kana') || '',
      prefecture_id: searchParams.get('prefecture_id') || '',
      page_num: Number(searchParams.get('page_num')) || 20,
    }),
    [searchParams]
  );

  const [filters, setFilters] = useState<InsurerAttentionSearchFilters>(initialFilters);
  const [selectedAttention, setSelectedAttention] = useState<InsurerAttention | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [pageContent, setPageContent] = useState<PageContent>({
    attentions: [],
    pagination: null,
  });

  const searchFilters = useMemo(
    () => (hasSearched ? initialFilters : null),
    [hasSearched, initialFilters]
  );

  const isSearchChanged = useMemo(() => {
    if (!searchFilters) return true;

    return (
      filters.insurer_number !== searchFilters.insurer_number ||
      filters.insurer_name !== searchFilters.insurer_name ||
      filters.insurer_name_kana !== searchFilters.insurer_name_kana ||
      filters.prefecture_id !== searchFilters.prefecture_id ||
      filters.page_num !== searchFilters.page_num
    );
  }, [filters, searchFilters]);

  const searchQuery = useMemo(
    () =>
      searchFilters
        ? {
            insurer_number: searchFilters.insurer_number || '',
            insurer_name: searchFilters.insurer_name || '',
            insurer_name_kana: searchFilters.insurer_name_kana || '',
            prefecture_id: (searchFilters.prefecture_id || '').toString(),
            page_num: searchFilters.page_num || 20,
            page: currentPage,
          }
        : null,
    [searchFilters, currentPage]
  );

  const { data: initResponse, isLoading: initLoading } = useMainInsurerAttentionInit();
  const {
    data: searchResponse,
    isLoading: searching,
    isError: isSearchError,
  } = useMainInsurerAttentionSearch(searchQuery);
  const { data: detailResponse, isLoading: detailLoading } = useMainInsurerAttentionDetail(
    isDetailModalOpen && selectedAttention ? selectedAttention.id : 0
  );
  const { mutate: downloadAttachment } = useMainInsurerAttentionDownload();

  const prefectures = initResponse?.data?.prefectures || [];

  useEffect(() => {
    if (!searchFilters || !searchResponse?.success || !searchResponse.data) {
      return;
    }

    if (
      typeof searchResponse.data === 'object' &&
      'data' in searchResponse.data &&
      'pagination' in searchResponse.data
    ) {
      const paginatedData = searchResponse.data as InsurerAttentionSearchPaginatedData;
      setPageContent({
        attentions: paginatedData.data,
        pagination: paginatedData.pagination,
      });
      return;
    }

    setPageContent({
      attentions: (searchResponse.data as InsurerAttention[]) || [],
      pagination: null,
    });
  }, [searchResponse, searchFilters]);

  const handleFilterChange = useCallback(
    (name: keyof InsurerAttentionSearchFilters, value: string | number) => {
      setFilters(prev => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleSubmit = useCallback(() => {
    const nextParams = new URLSearchParams();
    if (filters.insurer_number) nextParams.set('insurer_number', filters.insurer_number);
    if (filters.insurer_name) nextParams.set('insurer_name', filters.insurer_name);
    if (filters.insurer_name_kana) nextParams.set('insurer_name_kana', filters.insurer_name_kana);
    if (filters.prefecture_id) nextParams.set('prefecture_id', String(filters.prefecture_id));
    if (filters.page_num !== 20) nextParams.set('page_num', String(filters.page_num));
    nextParams.set('page', '1');
    nextParams.set('searched', '1');
    setSearchParams(nextParams);
    setSelectedAttention(null);
  }, [filters, setSearchParams]);

  const handlePageChange = useCallback(
    (page: number) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('page', String(page));
      setSearchParams(nextParams);
    },
    [searchParams, setSearchParams]
  );

  const handleDetailClick = useCallback((attention: InsurerAttention) => {
    setSelectedAttention(attention);
    setIsDetailModalOpen(true);
  }, []);

  const handleDetailModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedAttention(null);
  }, []);

  const handleDownloadAttachment = useCallback(
    (attachmentKey: AttachmentKey) => {
      if (!selectedAttention?.id) return;

      const filename = detailResponse?.data?.[attachmentKey];
      if (!filename) return;

      downloadAttachment({
        attachment: attachmentKey,
        id: selectedAttention.id,
        filename,
      });
    },
    [selectedAttention, detailResponse, downloadAttachment]
  );

  if (familySiteType && familySiteType !== 'medix') {
    return <NotFound />;
  }

  if (initLoading) {
    return (
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 rounded-md bg-white/80"
        aria-busy="true"
      >
        <AppSpin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <InsurerAttentionSearchForm
        filters={filters}
        prefectures={prefectures}
        searching={searching}
        isSearchChanged={isSearchChanged}
        onFilterChange={handleFilterChange}
        onSubmit={handleSubmit}
      />

      {isSearchError && (
        <AppAlert title="エラー" description={ERROR_MESSAGE} type="error" showIcon />
      )}

      {searchFilters && (
        <InsurerAttentionResults
          attentions={pageContent.attentions}
          pagination={pageContent.pagination}
          searching={searching}
          selectedAttentionId={selectedAttention?.id}
          onDetailClick={handleDetailClick}
          onPageChange={handlePageChange}
        />
      )}

      <InsurerAttentionDetailModal
        isOpen={isDetailModalOpen}
        loading={detailLoading}
        attention={detailResponse?.success ? detailResponse.data : undefined}
        onClose={handleDetailModalClose}
        onDownloadAttachment={handleDownloadAttachment}
      />
    </div>
  );
};

export default InsurerAttentionPage;
